import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { SecuredGroups } from 'Models/Security/UserGroups';
import { PassengerTypeKey, wizardModeOptions } from 'Models/Enums';
import { appConfigStore } from 'Models/AppConfigStore';
import { stringIsEmpty } from 'Util/TypeGuards';
import SecuredPage from 'Views/Components/Security/SecuredPage';
import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { PassengerDetailsList } from './PassengerDetailsList';
import passengerTypeStore from 'Models/PassengerTypeStore';
import {
	BookingWizardData,
	getPassengerTickets,
	setPassengerTickets,
} from '../../BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from '../../BookingWizardData/PassengerBookingWizardData';
import { isVehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { getDefaultPassengerInfo, PassengerDetail } from '../../BookingWizardData/PassengerDetail';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { combineAllPassengerTickets, getTotalPassengerCountFromWizardData } from './PassengerTabHelpers';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { useBookingSubjectName } from '../Shared/useBookingSubjectName';

export interface PassengersTabProps {}

export function PassengersTab() {
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	const isCreateMode = wizardData.wizardMode === wizardModeOptions.CREATE;

	// When passenger info is collected, the "name on booking" is captured on a passenger rather than
	// the Details tab. Pre-fill the first adult with the booking owner's name and flag it as the
	// primary contact — this is what serialises as the booking's primary name and drives the
	// "Primary contact" tag on the passenger card.
	const primaryContactOnPassenger =
		appConfigStore.passengerInfoEnabled &&
		appConfigStore.collectPrimaryBookingNameEnabled &&
		wizardData.innerWizardData instanceof PassengerBookingWizardData;
	const {
		firstName: ownerFirstName,
		lastName: ownerLastName,
		ready: ownerNameReady,
	} = useBookingSubjectName(wizardData.userId);
	const passengerCount = getTotalPassengerCountFromWizardData(wizardData);

	useEffect(() => {
		if (!primaryContactOnPassenger || !ownerNameReady || !setWizardData) return;
		const inner = wizardData.innerWizardData;
		if (!(inner instanceof PassengerBookingWizardData)) return;

		const allPassengers = combineAllPassengerTickets(wizardData);
		// Nothing to do once a passenger is already flagged (covers edits/alterations too).
		if (allPassengers.length === 0 || allPassengers.some(p => p.isPrimaryContact)) return;

		// First adult is the primary contact; fall back to the first passenger of any type.
		const target = inner.adultTickets?.[0] ?? allPassengers[0];
		const targetProp = passengerTypeStore.getWizardDataProperty(target.key as PassengerTypeKey);

		const newData = new BookingWizardData({ ...wizardData });
		setPassengerTickets(
			newData.innerWizardData,
			targetProp,
			(getPassengerTickets(newData.innerWizardData, targetProp) ?? []).map((p: PassengerDetail) =>
				p.id === target.id
					? new PassengerDetail({
							...p,
							isPrimaryContact: true,
							firstName: stringIsEmpty(p.firstName) ? ownerFirstName : p.firstName,
							lastName: stringIsEmpty(p.lastName) ? ownerLastName : p.lastName,
						})
					: p,
			),
		);
		setWizardData(newData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [primaryContactOnPassenger, ownerNameReady, ownerFirstName, ownerLastName, passengerCount]);

	const onSelectPreviousPassenger = (passenger: PassengerDetail, incompletePassengers: PassengerDetail[]) => {
		const passengerToReplace = incompletePassengers.find(x => x.key === passenger.key);
		const passengerAttributeName = passengerTypeStore.getBookingDtoProperty(passenger.key as PassengerTypeKey);

		// We need to copy the passenger object because we are going to modify it. The driver role belongs to
		// the slot being filled, not to the person picked from history: a walk-on booking can still hold the
		// flag from before a switch away from vehicle, and switching back must find it where it was.
		const passengerCopy = { ...passenger };
		passengerCopy.driver = passengerToReplace?.driver ?? false;
		const newPassengers = [
			...(getPassengerTickets(wizardData.innerWizardData, passengerAttributeName) ?? []).filter(
				(y: PassengerDetail) => y.id !== passengerToReplace?.id,
			),
			passengerCopy,
		];

		if (setWizardData) {
			setWizardData(
				new BookingWizardData({
					...wizardData,
					innerWizardData: {
						...wizardData.innerWizardData,
						[passengerAttributeName]: newPassengers,
					},
				}),
			);
		}
	};

	const onClearForm = (passenger: PassengerDetail) => {
		const newData = new BookingWizardData({ ...wizardData });

		const updatedPassenger = getDefaultPassengerInfo(passenger.key as PassengerTypeKey, passenger.driver);

		const passengerTypeProperty = passengerTypeStore.getWizardDataProperty(passenger.key as PassengerTypeKey);
		const remainingPassengers = (getPassengerTickets(newData.innerWizardData, passengerTypeProperty) ?? []).filter(
			(passengerInfo: PassengerDetail) => {
				return passengerInfo.id !== passenger.id;
			},
		);

		remainingPassengers.push(updatedPassenger);
		setPassengerTickets(newData.innerWizardData, passengerTypeProperty, remainingPassengers);

		// Only a driver on a vehicle booking keeps their name: it mirrors the vehicle's driver details. A walk-on
		// booking can still carry the flag from before a switch away from vehicle, and is cleared like anyone.
		if (passenger.driver && isVehicleBookingWizardData(wizardData.innerWizardData)) {
			updatedPassenger.firstName = passenger.firstName;
			updatedPassenger.lastName = passenger.lastName;
			updatedPassenger.phone = passenger.phone;
		}

		if (setWizardData) {
			setWizardData(newData as BookingWizardData);
		}
	};

	const title = isCreateMode
		? intl.formatMessage({
				id: 'passenger-tab__title__create',
				defaultMessage: 'Enter passenger details',
			})
		: intl.formatMessage({
				id: 'passenger-tab__title__edit',
				defaultMessage: 'Edit passenger details',
			});
	const subtitle = fmtOptional(
		{
			id: 'passenger-tab__subtitle',
			defaultMessage: '{count, plural, one {# passenger} other {# passengers}}',
		},
		{ count: passengerCount },
	);

	return (
		<SecuredPage groups={SecuredGroups.create.onlyLoggedIn().groups}>
			<div className="booking-wizard-tab passenger-details-tab">
				<div className="passenger-details-tab__container">
					<WizardStepHeader
						iconName="groups"
						title={title}
						subtitle={subtitle || undefined}
						showCloseLink={wizardData.wizardMode === wizardModeOptions.ALTERATION}
						bookingToEditId={wizardData?.bookingToEdit ?? ''}
					/>
					<PassengerDetailsList
						wizardData={wizardData}
						setWizardData={setWizardData}
						// TODO BW - fix this (errors)
						// errors={errors}
						onPreviousPassengerSelect={onSelectPreviousPassenger}
						onClearForm={(passenger: PassengerDetail) => onClearForm(passenger)}
					/>
				</div>
			</div>
		</SecuredPage>
	);
}
